import * as React from 'react';
import { noop } from '@deltasierra/utilities-object';
import { TypedExtract } from '@deltasierra/shared';
import { BootstrapColor, BootstrapSize } from '../theme';

export type ButtonProps = {
    /**
     * Whether to display this button as a block or inline
     *
     * @default false
     */
    block?: boolean;
    children?: React.ReactNode;
    className?: string;
    dataCy?: string;
    disabled?: boolean;
    id?: string;
    label?: string;
    onClick?: () => void;
    size?: TypedExtract<BootstrapSize, 'lg' | 'md' | 'sm' | 'xs'>;
    spaceLeft?: boolean;
    /**
     *
     */
    theme?: BootstrapColor;
    type?: 'button' | 'reset' | 'submit';
};

/**
 * The base button component. This accepts either a label or children.
 *
 * @example
 * // Button with a label
 * <Button label={{ keyId: 'COMMON.MY_BUTTON' }} />
 * @example
 * // Button with custom contents
 * <Button>
 *     <img src="..." />
 * </Button>
 * @param props - Props
 * @param props.children - Custom content for the button (either specify content or a label but not both)
 * @param props.theme - Color theme (refer to the bootstrap documentation for theme options)
 * @param props.label - The button's label (either specify content or a label but not both)
 * @param props.onClick - On click handler/callback
 * @param props.size - Button sizing (refer to the bootstrap documentation for size options)
 * @returns React.ReactElement
 */
export const Button: React.FunctionComponent<ButtonProps> = React.memo(props => {
    const {
        block = false,
        children,
        className = '',
        dataCy,
        disabled = false,
        id = '',
        label,
        onClick,
        size,
        spaceLeft = false,
        theme = 'default',
        type = 'button',
    } = props;
    const onClickHandlerOrDefault = onClick || noop;
    const memoizedOnClickHandler: () => void = React.useCallback<() => void>(
        () => onClickHandlerOrDefault(),
        [onClickHandlerOrDefault],
    );
    const themeClass = `btn-${theme}`;
    const sizeClass = size ? `btn-${size}` : '';
    const blockOrInlineClass = block ? 'btn-block' : '';
    return (
        <button
            className={`btn ${themeClass} ${sizeClass} ${blockOrInlineClass} ${
                spaceLeft ? 'space-left' : ''
            } ${className}`}
            data-cy={dataCy}
            disabled={disabled}
            id={id}
            type={type}
            onClick={memoizedOnClickHandler}
        >
            {label ? label : children}
        </button>
    );
});
Button.displayName = 'Button';
